import { useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/lib/auth';

import type { RetrievalPath } from '@/retrieve';

import { fetchQueryAnalytics } from '../api';
import type { QueryAnalytics } from '../types';

type RangeKey = '7d' | '14d' | '30d';

/** Rows charted under "Top queried repositories"; the API sends more, for filtering. */
const TOP_PACKAGES_SHOWN = 10;

function formatLatency(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function formatChars(chars: number): string {
  return chars >= 1000 ? `${(chars / 1000).toFixed(1)}K` : String(chars);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function formatDuration(ms: number): string {
  if (ms < 60_000) return formatLatency(Math.round(ms));
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`;
}

const PATH_LABELS: Record<RetrievalPath, string> = {
  match: 'Search match',
  parent: 'Parent',
  edge: 'Call/import edge',
  child: 'Child',
  type: 'Type reference',
};

/** A count with its unique share: "12 (5 unique)". */
function WithUnique({ total, unique }: { total: number; unique: number }) {
  return (
    <>
      {total.toLocaleString()}
      <span className="text-dim"> ({unique.toLocaleString()})</span>
    </>
  );
}

/**
 * Per-path funnel: which candidates the chunk picker saw, chose and the request
 * delivered. Parenthesized counts are rows only that path produced — a path
 * whose unique rows are rarely picked is cost, not recall.
 */
function RetrievalFunnel({
  retrieval,
  range,
}: {
  retrieval: QueryAnalytics['retrieval'];
  range: string;
}) {
  const th = 'px-2 py-1.5 text-left font-medium text-dim';
  const td = 'px-2 py-1.5 font-mono text-ink';
  return (
    <div data-testid="retrieval-funnel" className="sheet space-y-4 p-4">
      <div>
        <h4 className="text-xs font-medium uppercase tracking-wider text-dim">
          Retrieval paths ({range})
        </h4>
        <p className="mt-0.5 text-xs text-dim">
          {retrieval.requests
            ? `${retrieval.requests.toLocaleString()} requests · ${retrieval.retrievals.toLocaleString()} retrievals · picker chose nothing in ${retrieval.picker_fallbacks.toLocaleString()} · (n) = rows only that path produced`
            : 'Collecting from new queries'}
        </p>
      </div>
      {retrieval.requests > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-rule">
                  <th className={th}>Path</th>
                  <th className={th}>Candidates</th>
                  <th className={th}>Picked</th>
                  <th className={th}>Pick rate</th>
                  <th className={th}>Unique pick rate</th>
                  <th className={th}>Delivered</th>
                  <th
                    className={th}
                    title="Billed picker prompt tokens spent on rows only this path produced, apportioned by their share of the picker tree"
                  >
                    Picker tokens (unique)
                  </th>
                </tr>
              </thead>
              <tbody>
                {retrieval.paths.map((p) => (
                  <tr key={p.path} className="border-b border-rule/50">
                    <td className="px-2 py-1.5 text-ink">{PATH_LABELS[p.path]}</td>
                    <td className={td}>
                      <WithUnique total={p.candidates} unique={p.unique} />
                    </td>
                    <td className={td}>
                      <WithUnique total={p.picked} unique={p.unique_picked} />
                    </td>
                    <td className={td}>{formatRate(p.pick_rate)}</td>
                    <td className={td}>{formatRate(p.unique_pick_rate)}</td>
                    <td className={td}>
                      <WithUnique total={p.delivered} unique={p.unique_delivered} />
                    </td>
                    <td className={td}>
                      {p.unique_prompt_tokens === null
                        ? '—'
                        : p.unique_prompt_tokens.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {retrieval.expansions.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-rule">
                    <th className={th}>Expansion</th>
                    <th
                      className={th}
                      title="Share of requests where the expansion found links from its sources"
                    >
                      Fire rate
                    </th>
                    <th className={th}>Ran in</th>
                    <th className={th}>Rows added</th>
                    <th className={th}>Avg SQL time</th>
                  </tr>
                </thead>
                <tbody>
                  {retrieval.expansions.map((e) => (
                    <tr key={e.path} className="border-b border-rule/50">
                      <td className="px-2 py-1.5 text-ink">{PATH_LABELS[e.path]}</td>
                      <td className={td}>{formatRate(e.fire_rate)}</td>
                      <td className={td}>{e.requests_ran.toLocaleString()} requests</td>
                      <td className={td}>{e.added.toLocaleString()}</td>
                      <td className={td}>{e.avg_ms === null ? '—' : formatLatency(e.avg_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="sheet p-4">
      <p className="text-2xs font-medium uppercase tracking-wider text-dim">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-ink">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-dim">{sub}</p>}
    </div>
  );
}

function BarChart({
  data,
  labelKey,
  valueKey,
}: {
  data: { [k: string]: unknown }[];
  labelKey: string;
  valueKey: string;
}) {
  if (data.length === 0) return <p className="text-xs text-dim">No data yet.</p>;
  const maxVal = Math.max(...data.map((d) => Number(d[valueKey]) || 0), 1);
  return (
    <div className="space-y-1.5">
      {data.map((d, i) => {
        const val = Number(d[valueKey]) || 0;
        const pct = (val / maxVal) * 100;
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className="w-28 shrink-0 truncate text-dim">{String(d[labelKey])}</span>
            <div className="flex-1 h-5 rounded bg-bg overflow-hidden">
              <div
                className="h-full rounded bg-accent/70 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-10 text-right font-mono text-dim">{val}</span>
          </div>
        );
      })}
    </div>
  );
}

function MiniSparkline({ data }: { data: { date: string; count: number }[] }) {
  if (data.length === 0) return <p className="text-xs text-dim">No data yet.</p>;
  const maxVal = Math.max(...data.map((d) => d.count), 1);
  const h = 60;
  const w = data.length * 12;
  const points = data
    .map((d, i) => `${i * 12 + 6},${h - (d.count / maxVal) * (h - 8) - 4}`)
    .join(' ');
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${Math.max(w, 100)} ${h}`}
        className="w-full max-w-md"
        preserveAspectRatio="none"
      >
        <polyline fill="none" stroke="rgb(var(--accent))" strokeWidth={2} points={points} />
        {/*
          One invisible hover band per day, each owning its own <title>. As bare
          children of <svg> these titles were siblings of the polyline rather than
          children of any shape, so only the first was used — as the accessible
          name for the whole chart — and hovering anywhere reported day one's count.
          `transparent` rather than `none`: fill="none" takes no pointer events.
        */}
        {data.map((d, i) => (
          <rect key={i} x={i * 12} y={0} width={12} height={h} fill="transparent">
            <title>
              {d.date}: {d.count}
            </title>
          </rect>
        ))}
      </svg>
      <div className="flex justify-between text-3xs text-dim max-w-md">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function RangeToggle({
  active,
  value,
  onClick,
  children,
}: {
  active: boolean;
  value: RangeKey;
  onClick: (v: RangeKey) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={`px-2 py-1 text-xs font-medium transition-colors ${
        active ? 'bg-accent text-white' : 'text-link hover:bg-bg'
      }`}
    >
      {children}
    </button>
  );
}

export function AnalyticsPanel() {
  const { getToken } = useAuth();
  const [analytics, setAnalytics] = useState<QueryAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKey>('30d');
  const [packageFilter, setPackageFilter] = useState('');

  useEffect(() => {
    let active = true;
    void (async () => {
      setIsLoading(true);
      try {
        const token = await getToken();
        if (!token) return;
        const days = range === '7d' ? 7 : range === '14d' ? 14 : 30;
        const result = await fetchQueryAnalytics(token, days);
        if (active) {
          setAnalytics(result);
          setError(null);
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken, range]);

  // The server sends a deep list precisely so this filter can reach past the
  // charted rows; slice only after filtering, or searching for a package outside
  // the visible top 10 comes back empty.
  const filteredTopPackages = useMemo(() => {
    if (!analytics) return [];
    const q = packageFilter.trim().toLowerCase();
    const matches = q
      ? analytics.top_packages.filter((p) => String(p.package_name).toLowerCase().includes(q))
      : analytics.top_packages;
    return matches.slice(0, TOP_PACKAGES_SHOWN);
  }, [analytics, packageFilter]);

  if (isLoading) {
    return <div className="py-8 text-center text-sm text-dim">Loading analytics…</div>;
  }

  if (error) {
    return <div className="border border-del/30 bg-del/15 p-4 text-sm text-del">{error}</div>;
  }

  if (!analytics) return null;

  const feedbackTotal = analytics.feedback_positive + analytics.feedback_negative;
  const satisfactionPct = feedbackTotal
    ? Math.round((analytics.feedback_positive / feedbackTotal) * 100)
    : null;

  return (
    <div data-testid="analytics-panel" className="space-y-6">
      {/* Header with range toggle */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-ink">Query analytics</h3>
        <div className="flex items-center gap-0.5 border border-rule bg-panel p-0.5">
          <RangeToggle active={range === '7d'} value="7d" onClick={setRange}>
            7d
          </RangeToggle>
          <RangeToggle active={range === '14d'} value="14d" onClick={setRange}>
            14d
          </RangeToggle>
          <RangeToggle active={range === '30d'} value="30d" onClick={setRange}>
            30d
          </RangeToggle>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label={`Total queries (${range})`} value={analytics.total_queries} />
        <StatCard
          label="Today"
          value={analytics.queries_today}
          sub={`${analytics.queries_this_week} this week`}
        />
        <StatCard
          label="Avg latency"
          value={formatLatency(analytics.avg_latency_ms)}
          sub={`Across ${analytics.total_queries.toLocaleString()} queries in ${range}`}
        />
        <StatCard
          label="Avg output context"
          value={
            analytics.avg_output_chars === null
              ? '—'
              : `~${formatChars(Math.round(analytics.avg_output_chars / 4))} tokens`
          }
          sub={
            analytics.output_size_samples
              ? `${formatChars(analytics.avg_output_chars ?? 0)} chars · ${analytics.avg_chunks} avg chunks`
              : 'Collecting from new queries'
          }
        />
        <StatCard
          label="Satisfaction"
          value={satisfactionPct !== null ? `${satisfactionPct}%` : '—'}
          sub={
            feedbackTotal
              ? `${analytics.feedback_positive} 👍 · ${analytics.feedback_negative} 👎 (${feedbackTotal} rated)`
              : 'No feedback yet'
          }
        />
      </div>

      <div className="space-y-3">
        <h4 className="text-xs font-medium uppercase tracking-wider text-dim">Index footprint</h4>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard
            label="Indexed packages"
            value={analytics.package_index.package_count.toLocaleString()}
            sub={
              analytics.package_index.source_size_samples
                ? `${analytics.package_index.total_files.toLocaleString()} files across ${analytics.package_index.source_size_samples.toLocaleString()} measured packages`
                : 'Collecting footprint from new indexes'
            }
          />
          <StatCard
            label="Indexed source size"
            value={
              analytics.package_index.source_size_samples
                ? formatBytes(analytics.package_index.total_source_bytes)
                : '—'
            }
            sub="Readable source text"
          />
          <StatCard
            label="Stored payload"
            value={
              analytics.package_index.payload_size_samples
                ? formatBytes(analytics.package_index.total_payload_bytes)
                : '—'
            }
            sub={`Page indexes + chunks · ${analytics.package_index.payload_size_samples.toLocaleString()} measured`}
          />
          <StatCard
            label={`Avg indexing time (${range})`}
            value={
              analytics.package_index.avg_index_duration_ms === null
                ? '—'
                : formatDuration(analytics.package_index.avg_index_duration_ms)
            }
            sub={`${analytics.package_index.duration_samples.toLocaleString()} completed jobs`}
          />
          <StatCard
            label="Nodes / vectors"
            value={`${analytics.package_index.node_samples ? analytics.package_index.total_nodes.toLocaleString() : '—'} / ${analytics.package_index.total_vectors.toLocaleString()}`}
            sub={`${analytics.package_index.total_chunks.toLocaleString()} measured chunks · ${analytics.package_index.vector_packages.toLocaleString()} vectorized packages`}
          />
        </div>
      </div>

      <RetrievalFunnel retrieval={analytics.retrieval} range={range} />

      {/* Daily trend */}
      <div className="sheet p-4">
        <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-dim">
          Daily queries ({range})
        </h4>
        <MiniSparkline data={analytics.daily_counts} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Top packages */}
        <div className="sheet p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h4 className="text-xs font-medium uppercase tracking-wider text-dim">
              Top queried repositories
            </h4>
            <input
              type="search"
              value={packageFilter}
              onChange={(e) => setPackageFilter(e.target.value)}
              placeholder="Filter…"
              className="w-32 rounded border border-rule bg-bg px-2 py-1 text-xs text-link"
            />
          </div>
          <BarChart data={filteredTopPackages} labelKey="package_name" valueKey="count" />
        </div>

        {/* By source */}
        <div className="sheet p-4">
          <h4 className="mb-3 text-xs font-medium uppercase tracking-wider text-dim">
            Queries by source
          </h4>
          <BarChart data={analytics.by_source} labelKey="source" valueKey="count" />
        </div>
      </div>
    </div>
  );
}
